import { DateTime } from "luxon";

/**
 * Every date the interface shows is rendered in the *household's* timezone, not
 * the device's. A phone in another timezone must still say the birthday is on
 * the 30th — spec §6.4.
 */

export const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function inZone(iso: string, zone: string): DateTime {
  return DateTime.fromISO(iso, { zone });
}

export function today(zone: string): string {
  return DateTime.now().setZone(zone).toISODate() ?? "";
}

export function addDays(date: string, days: number, zone: string): string {
  return DateTime.fromISO(date, { zone }).plus({ days }).toISODate() ?? "";
}

/** Sunday-first, matching the design's week grid. */
export function weekStart(date: string, zone: string): string {
  const d = DateTime.fromISO(date, { zone });
  return d.minus({ days: d.weekday % 7 }).toISODate() ?? "";
}

export function monthStart(date: string, zone: string): string {
  return DateTime.fromISO(date, { zone }).startOf("month").toISODate() ?? "";
}

/** "Aug 25 – 31", or "Aug 30 – Sep 5" when the week straddles a month. */
export function weekLabel(start: string, zone: string): string {
  const from = DateTime.fromISO(start, { zone });
  const to = from.plus({ days: 6 });
  const left = from.toFormat("LLL d");
  const right = from.month === to.month ? to.toFormat("d") : to.toFormat("LLL d");
  return `${left} – ${right}`;
}

export function monthLabel(date: string, zone: string): string {
  return DateTime.fromISO(date, { zone }).toFormat("LLLL");
}

export function yearOf(date: string, zone: string): string {
  return DateTime.fromISO(date, { zone }).toFormat("yyyy");
}

export function dayName(date: string, zone: string): string {
  return DateTime.fromISO(date, { zone }).toFormat("ccc");
}

export function dayNumber(date: string, zone: string): string {
  return DateTime.fromISO(date, { zone }).toFormat("d");
}

/** "4:30p" — compact, for dense rows. */
export function shortTime(iso: string, zone: string): string {
  const t = inZone(iso, zone);
  return t.minute === 0 ? t.toFormat("h':00'a").toLowerCase() : t.toFormat("h:mma").toLowerCase();
}

/** "4:30 pm" — for the day view and meta lines. */
export function longTime(iso: string, zone: string): string {
  return inZone(iso, zone).toFormat("h:mm a").toLowerCase();
}

/** The calendar date an occurrence belongs to, household-local. */
export function dateOf(iso: string, zone: string): string {
  return inZone(iso, zone).toISODate() ?? "";
}

/**
 * The longest a single occurrence may run before this stops walking days. The
 * server carries the same ceiling; it is here so a bad payload cannot spin a
 * loop on the wall screen.
 */
const MAX_SPAN_DAYS = 400;

/**
 * Every date an occurrence covers. The mirror of the server's
 * `datesCoveredBy`, and it has to stay one: the two answers appear on the same
 * wall, one from the display payload and one from the phone in your hand.
 *
 * The end is exclusive either way. A trip "back Thursday" is stored as ending
 * at Thursday midnight and does not cover Thursday; a flight landing at
 * midnight ends on the day it took off. Timed events span too — a red-eye
 * belongs on both of its days.
 */
export function datesCovered(starts: string, ends: string | null, zone: string): string[] {
  const first = dateOf(starts, zone);
  if (ends === null) return [first];

  const last = inZone(ends, zone).minus({ milliseconds: 1 });
  const out: string[] = [];
  let cursor = DateTime.fromISO(first, { zone });

  while (cursor <= last && out.length < MAX_SPAN_DAYS) {
    out.push(cursor.toISODate() ?? "");
    cursor = cursor.plus({ days: 1 });
  }

  return out.length === 0 ? [first] : out;
}

/** "all day", "4:30 pm", or "4:30 – 6:00 pm". */
export function timeLabel(
  starts: string,
  ends: string | null,
  allDay: boolean,
  zone: string,
): string {
  if (allDay) return "all day";
  if (ends === null) return longTime(starts, zone);
  return `${inZone(starts, zone).toFormat("h:mm").toLowerCase()} – ${longTime(ends, zone)}`;
}

/**
 * "Wednesday, August 26", or "Wednesday, August 26 – Friday, August 28".
 *
 * Ends are stored exclusive, so the last day someone is actually away is the
 * day before the stored end. Getting that wrong adds a day to every trip,
 * which is why every surface that prints a range uses this one function.
 *
 * A timed event gets a range too when it crosses midnight — the overnight
 * flight is on both days, and saying only the day it left is the same lie in
 * a different spelling.
 */
export function dayRangeLabel(starts: string, ends: string | null, zone: string): string {
  const first = inZone(starts, zone);
  const format = "cccc, LLLL d";
  if (ends === null) return first.toFormat(format);

  const last = inZone(ends, zone).minus({ milliseconds: 1 });
  if (last.toISODate() === first.toISODate() || last < first) return first.toFormat(format);
  return `${first.toFormat(format)} – ${last.toFormat(format)}`;
}
